import { useUserDataStore } from '@/store/userDataStore';
import { authService } from '@/services/authService';
import { useBottomSheetStore } from '@/store/bottomSheetStore';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { RunModalRoutes } from '@/router/modalRoutes';
import { exchangeCodeForToken, loginWithOAuth } from '@/services/oauthservice';

import type { RouteLocationNormalized } from 'vue-router';

import MembershipInfoPopUp from '@/components/MobileAppComponents/MembershipInfoPopUp.vue';

const webRoutes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/:lang(en|fr|ar|es)?', // Added to handle optional lang prefix
    redirect: (to: { params: { lang?: string } }) => {
      const lang = to.params.lang ? `/${to.params.lang}` : '';
      return `${lang}/dashboard`;
    },
    component: () => import('@/layouts/DefaultLayout.vue'),
    meta: { RequiresAuth: true, LogUserOut: false },
    children: [
      {
        path: 'dashboard',
        name: 'Feed',
        component: () => import('@/views/SitePages/Dashboard.vue'),
        meta: { hideCommunitiesWidget: true, LogUserOut: false }, // Add this line to hide the communities widget
      },
      {
        path: 'app_login',
        name: 'Login Page',
        component: () => import('@/views/SitePages/Login.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'menu_login',
        name: 'Menu Login',
        meta: { RequiresAuth: false, LogUserOut: true },
        component: () => import('@/views/SitePages/Login.vue'),
      },
      {
        path: 'login',
        name: 'Login',
        meta: { RequiresAuth: false, LogUserOut: true },
        component: () => import('@/views/SitePages/Login.vue'),
      },
      {
        // New registration-flow redesign, kept on its own route so the existing app_login/menu_login
        // page above is untouched and both can be tested side by side.
        path: 'login-new',
        name: 'Login (New)',
        component: () => import('@/views/SitePages/LoginNew.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        // New registration-flow redesign, kept off the existing /register path (which stays on
        // the old/classic registration) so both can be tested side by side.
        path: 'register-new',
        name: 'Create Account',
        component: () => import('@/views/SitePages/Register.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'register-new/results',
        name: 'Find Your Name',
        component: () => import('@/views/SitePages/RegisterSearchResults.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'register-new/claim',
        name: 'Claim Profile',
        component: () => import('@/views/SitePages/RegisterClaim.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'register-new/new',
        name: 'New Profile',
        component: () => import('@/views/SitePages/RegisterNew.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        // Shared by both login-new and the register-new claim/fresh-profile steps — reads
        // whatever player context (if any) is already in registrationStore.
        path: 'register-new/partners',
        name: 'Partner Sign-in',
        component: () => import('@/views/SitePages/RegisterPartnerPicker.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'register-new/verify',
        name: 'Verify Email',
        component: () => import('@/views/SitePages/RegisterVerify.vue'),
        // LogUserOut: false (unlike the other register/* screens) — clicking the emailed verify
        // link logs the user in via the returned JWT; forcing a logout here would immediately
        // undo that.
        meta: { RequiresAuth: false, LogUserOut: false },
      },
      {
        path: 'skillstesting',
        name: 'Skills Testing',
        component: () => import('@/views/SitePages/SkillsTesting.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'players',
        name: 'Player Ratings',
        component: () => import('@/views/SitePages/Players.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'info',
        name: 'Info',
        component: () => import('@/views/AdminPages/Info.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'americano',
        name: 'Americano',
        component: () => import('@/views/SitePages/Americano.vue'),
        meta: { LogUserOut: false },
        beforeEnter: (to: RouteLocationNormalized) => {
          if (to.query.americanoId) {
            return { path: '/americano-detail', query: to.query };
          }
        },
      },
      {
        path: 'americano-detail',
        name: 'Americano Detail',
        component: () => import('@/views/SitePages/AmericanoDetail.vue'),
        meta: { LogUserOut: false },
      },
      {
        // Round Robin is the singles mode of the same Americano feature - same page/components, different URL.
        path: 'round-robin',
        name: 'Round Robin',
        component: () => import('@/views/SitePages/Americano.vue'),
        meta: { LogUserOut: false },
        beforeEnter: (to: RouteLocationNormalized) => {
          if (to.query.americanoId) {
            return { path: '/round-robin-detail', query: to.query };
          }
        },
      },
      {
        path: 'round-robin-detail',
        name: 'Round Robin Detail',
        component: () => import('@/views/SitePages/AmericanoDetail.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'boxes',
        name: 'Boxes',
        component: () => import('@/views/SitePages/Boxes.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'box',
        name: 'Box League',
        component: () => import('@/views/SitePages/BoxDetail.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'court_analysis',
        name: 'Court Analysis',
        component: () => import('@/views/SitePages/CourtAnalysis.vue'),
        meta: { RequiresAuth: true, LogUserOut: false },
      },
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/SitePages/Search.vue'),
        meta: { hideSearchWidget: true, LogUserOut: false }, // Add this line to hide the search widget
      },
      {
        path: 'invites',
        name: 'Invites',
        component: () => import('@/views/SitePages/Invites.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'add_result',
        name: 'Add Result',
        component: () => import('@/views/SitePages/AddResult.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'add_new_community',
        name: 'Add New Community',
        component: () => import('@/views/ConfigurationPages/Add_New_Community.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'what_if',
        name: 'What If',
        component: () => import('@/views/SitePages/WhatIf.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'calendar',
        name: 'Calendar',
        component: () => import('@/views/SitePages/Calendar.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'calendar_submit',
        name: 'Calendar Submit',
        component: () => import('@/views/SitePages/CalendarSubmit.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'find_a_match',
        name: 'Find A Match',
        component: () => import('@/views/SitePages/FindMatch.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'player_compare',
        name: 'Player Compare',
        component: () => import('@/views/SitePages/PlayerCompare.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'player_detail',
        name: 'Player Profile',
        component: () => import('@/views/SitePages/PlayerDetail.vue'),
        meta: { LogUserOut: false },
      },
      {
        // Any classic page not given its own route below falls through here and uses
        // the VITE_USE_CLASSIC_PROXY default (see axiosConfig.ts#getClassicBaseURL).
        // To move a specific page off the proxy (or back onto it) ahead of the rest,
        // add a static route for its path with `meta.useClassicProxy` set explicitly —
        // static routes always win over this catch-all, e.g.:
        //   { path: 'some_page', component: () => import('@/views/AdminPages/DynamicContent.vue'), meta: { useClassicProxy: false, LogUserOut: false } },
        path: ':catchAll(.*)',
        name: '',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'player_detail',
        name: 'player_detail',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { LogUserOut: false, useClassicProxy: true }, // This route is restricted to the web version only
      },
      {
        path: 'subscribe',
        name: 'Subscribe',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { WebRestricted: true, LogUserOut: false }, // This route is restricted to the web version only
      },
      {
        path: 'membership',
        name: 'Membership',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { WebRestricted: true, LogUserOut: false }, // This route is restricted to the web version only
      },
      {
        path: 'debug_app',
        name: 'Debug App',
        component: () => import('@/components/TestingComponents/UserDebugPage.vue'),
      },
    ],
  },
  {
    path: '/print',
    component: () => import('@/layouts/PrintLayout.vue'),
    meta: { RequiresAuth: false, LogUserOut: false },
    children: [
      {
        path: 'box_print',
        name: 'Boxes Print',
        component: () => import('@/views/SitePages/BoxPrint.vue'),
        meta: { LogUserOut: false },
      },
    ],
  },
  {
    path: '/oauth/callback',
    name: 'OAuthCallback',
    component: () => import('@/views/SitePages/Dashboard.vue'),
    beforeEnter: async (to: any, from: any, next: any) => {
      await exchangeCodeForToken(to.query.code as string);
      next('/dashboard');
    },
    meta: { RequiresAuth: false, LogUserOut: false },
  },
  {
    path: '/oauth_login',
    name: 'OAuthLogin',
    beforeEnter: async (to: any, from: any, next: any) => {
      await loginWithOAuth();
    },
    component: () => import('@/views/SitePages/Dashboard.vue'),
    meta: { RequiresAuth: false, LogUserOut: false },
  },
  {
    path: '/ref',
    name: 'ExternalReferral',
    component: () => import('@/views/SitePages/ExternalReferral.vue'),
    meta: { RequiresAuth: false, LogUserOut: false },
  },
  {
    // Top-level (not nested under DefaultLayout, same as /ref and /oauth_login above) —
    // this is a full-bleed splash and can't coexist with the app's persistent top bar/bottom nav.
    path: '/welcome',
    name: 'Welcome',
    component: () => import('@/views/SitePages/Welcome.vue'),
    meta: { RequiresAuth: false, LogUserOut: true },
  },
  {
    // Landed on by LevelTechSsoCallbackController (squash-levels-laravel) after a failed
    // SSO login — top-level for the same reason as /welcome above.
    path: '/sso-error',
    name: 'SsoError',
    component: () => import('@/views/SitePages/SsoError.vue'),
    meta: { RequiresAuth: false, LogUserOut: true },
  },
  {
    // Reached from Login/LoginNew when api/login returns THIRD_PARTY_ACCOUNT_NO_PASSWORD
    // (account provisioned via SSO/partner auth, no real password set) — top-level for the
    // same reason as /welcome above.
    path: '/set-password',
    name: 'SetPassword',
    component: () => import('@/views/SitePages/SetPassword.vue'),
    meta: { RequiresAuth: false, LogUserOut: true },
  },
];

const PerformWebRules = async (to: any, from: any) => {
  const userDataStore = useUserDataStore();
  const isApp = isMobileApp();

  if (isApp && userDataStore.initialized && userDataStore.isLoggedOut && to.meta.RequiresAuth) {
    return '/app_login';
  }

  if (isApp && to.meta.WebRestricted) {
    const sheet = useBottomSheetStore();
    sheet.openSheet({
      id: 'membership-info',
      component: MembershipInfoPopUp,
      options: {
        bgColor: 'primary',
        siteLogo: true,
        showOnDesktop: true,
        disablePersistence: true,
      },
    });
    return false; // Prevent navigation
  }
};

const WebAfterRules = async (to: any, from: any) => {
  const isApp = isMobileApp();

  if (isApp && to.meta.LogUserOut) {
    await authService.logout();
  }

  await RunModalRoutes(to, from);
};

export { PerformWebRules, WebAfterRules, webRoutes };
